export type Direction = 'ltr' | 'rtl';

/**
 * - `ready`: source language, final copy.
 * - `needs-native-review`: complete, but §23.7 requires native speakers to write it before launch.
 * - `stub`: partial; missing keys fall back to English.
 */
export type TranslationStatus = 'ready' | 'needs-native-review' | 'stub';

export interface LocaleInfo {
  code: string;
  dir: Direction;
  nativeName: string;
  englishName: string;
  /** Tag passed to Intl formatters. */
  intl: string;
  status: TranslationStatus;
}

export const LOCALES = {
  en: {
    code: 'en',
    dir: 'ltr',
    nativeName: 'English',
    englishName: 'English',
    intl: 'en',
    status: 'ready',
  },
  de: {
    code: 'de',
    dir: 'ltr',
    nativeName: 'Deutsch',
    englishName: 'German',
    intl: 'de',
    status: 'needs-native-review',
  },
  ar: {
    code: 'ar',
    dir: 'rtl',
    nativeName: 'العربية',
    englishName: 'Arabic',
    // Latin digits keep times, prices and device codes consistent across the product (ADR 0404).
    intl: 'ar-u-nu-latn',
    status: 'needs-native-review',
  },
  tr: {
    code: 'tr',
    dir: 'ltr',
    nativeName: 'Türkçe',
    englishName: 'Turkish',
    intl: 'tr',
    status: 'stub',
  },
} as const satisfies Record<string, LocaleInfo>;

export type LocaleCode = keyof typeof LOCALES;
export const LOCALE_CODES = Object.keys(LOCALES) as LocaleCode[];
export const SOURCE_LOCALE: LocaleCode = 'en';

export function isLocaleCode(value: string): value is LocaleCode {
  return Object.hasOwn(LOCALES, value);
}

export function getDirection(locale: LocaleCode): Direction {
  return LOCALES[locale].dir;
}

export function isRtl(locale: LocaleCode): boolean {
  return LOCALES[locale].dir === 'rtl';
}

/**
 * Picks the best supported locale from a preference list such as `navigator.languages` or
 * expo-localization's `getLocales()` tags. Matches the full tag first, then the language subtag.
 */
export function negotiateLocale(
  requested: readonly string[],
  fallback: LocaleCode = SOURCE_LOCALE,
): LocaleCode {
  for (const tag of requested) {
    const lower = tag.toLowerCase();
    if (isLocaleCode(lower)) return lower;
    const language = lower.split(/[-_]/)[0] ?? '';
    if (isLocaleCode(language)) return language;
  }
  return fallback;
}
